import React, { useEffect, useRef, useState } from "react";
import { styled } from "@mui/material/styles";
import {
    Typography,
    Box,
    Container,
    Grid,
    Chip,
    Button,
    Card,
    CardContent,
} from "@mui/material";
import { Fade } from "react-awesome-reveal";
import { useNavigate } from "react-router-dom";

// ── MUI Icons ──────────────────────────────────────────────────
import EmojiEventsIcon        from "@mui/icons-material/EmojiEvents";
import CodeIcon                from "@mui/icons-material/Code";
import RocketLaunchIcon        from "@mui/icons-material/RocketLaunch";
import PsychologyIcon          from "@mui/icons-material/Psychology";
import StarIcon                from "@mui/icons-material/Star";
import WorkspacePremiumIcon    from "@mui/icons-material/WorkspacePremium";
import TrendingUpIcon          from "@mui/icons-material/TrendingUp";
import GroupsIcon              from "@mui/icons-material/Groups";
import LightbulbIcon           from "@mui/icons-material/Lightbulb";
import BusinessCenterIcon      from "@mui/icons-material/BusinessCenter";
import ArchitectureIcon        from "@mui/icons-material/Architecture";
import BugReportIcon           from "@mui/icons-material/BugReport";
import DevicesIcon             from "@mui/icons-material/Devices";
import SupportAgentIcon        from "@mui/icons-material/SupportAgent";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import VerifiedIcon            from "@mui/icons-material/Verified";
import SchoolIcon              from "@mui/icons-material/School";
import AutoAwesomeIcon         from "@mui/icons-material/AutoAwesome";
import PublicIcon from '@mui/icons-material/Public';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import BoltIcon from '@mui/icons-material/Bolt';
// ============================================================
//  BRAND CONSTANTS
//  Keep ALL colors here — update once and it changes everywhere.
//  Never hardcode hex values inside JSX or styled components.
// ============================================================
const BRAND = {
    dark:      "#100720",                    // Deep purple-black — main background
    gold:      "#E5C95F",                    // Goldenrod — primary accent
    goldenrod: "#DAA520",                    // Pure goldenrod — secondary accent
    white:     "#FFFFFF",                    // Pure white — headings and labels
    dimWhite:  "rgba(255,255,255,0.62)",     // Muted white — body/subtext
    cardBg:    "rgba(255,255,255,0.04)",     // Semi-transparent card background
    border:    "rgba(229,201,95,0.2)",       // Gold-tinted border
};

// ============================================================
//  STYLED COMPONENTS
//  Define reusable MUI styled wrappers here.
//  Each has a comment explaining its purpose and props.
// ============================================================

/**
 * PageWrapper
 * Root Box for the entire Vision page.
 * Sets the deep dark background and hides horizontal overflow
 * caused by animated elements entering from off-screen.
 */
const PageWrapper = styled(Box)({
    width: "100%",
    minHeight: "100vh",
    backgroundColor: BRAND.dark,
    fontFamily: "'Poppins', sans-serif",
    overflowX: "hidden",
});

/**
 * HeroSection
 * Full-viewport hero area.
 * Uses ::before for a large radial gold glow behind the text,
 * and ::after for a subtle grid texture overlay.
 */
const HeroSection = styled(Box)({
    position: "relative",
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    overflow: "hidden",
    "&::before": {
        content: '""',
        position: "absolute",
        top: "-100px",
        left: "50%",
        transform: "translateX(-50%)",
        width: "900px",
        height: "600px",
        borderRadius: "50%",
        background:
            "radial-gradient(ellipse, rgba(229,201,95,0.13) 0%, rgba(218,165,32,0.05) 45%, transparent 70%)",
        pointerEvents: "none",
    },
    "&::after": {
        content: '""',
        position: "absolute",
        inset: 0,
        backgroundImage:
            "linear-gradient(rgba(229,201,95,0.025) 1px, transparent 1px)," +
            "linear-gradient(90deg, rgba(229,201,95,0.025) 1px, transparent 1px)",
        backgroundSize: "60px 60px",
        pointerEvents: "none",
    },
});

/**
 * GoldChip
 * Small pill-shaped badge used for section labels and eyebrow text.
 * Extends MUI Chip — always gold-tinted on dark background.
 */
const GoldChip = styled(Chip)({
    backgroundColor: "rgba(229,201,95,0.1)",
    border: `1px solid ${BRAND.border}`,
    color: BRAND.gold,
    fontFamily: "'Poppins', sans-serif",
    fontWeight: 600,
    fontSize: "0.68rem",
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    "& .MuiChip-icon": { color: BRAND.gold },
});

/**
 * SectionWrapper
 * Full-width section container used between the hero and footer.
 * Pass variant="alt" for a slightly lighter background —
 * this creates visual rhythm between alternating sections.
 */
const SectionWrapper = styled(Box)(({ variant }) => ({
    width: "100%",
    padding: "80px 0",
    backgroundColor:
        variant === "alt" ? "rgba(255,255,255,0.025)" : BRAND.dark,
    borderTop: `1px solid ${BRAND.border}`,
}));

/**
 * GoldDivider
 * A short decorative gold line placed under section headings.
 * Gives a premium look consistent with the brand palette.
 */
const GoldDivider = styled(Box)({
    width: "56px",
    height: "3px",
    background: `linear-gradient(90deg, ${BRAND.gold}, ${BRAND.goldenrod})`,
    borderRadius: "2px",
    margin: "14px auto 0",
});

/**
 * FeatureCard
 * Dark glass-morphism card used for identity cards and services.
 * Lifts and glows gold on hover using transform + box-shadow.
 * height: "100%" ensures all cards in a Grid row are equal height.
 */
const FeatureCard = styled(Card)({
    backgroundColor: BRAND.cardBg,
    border: `1px solid ${BRAND.border}`,
    borderRadius: "16px",
    backdropFilter: "blur(10px)",
    transition: "transform 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease",
    cursor: "default",
    height: "100%",
    "&:hover": {
        transform: "translateY(-6px)",
        borderColor: BRAND.gold,
        boxShadow: `0 20px 40px rgba(229,201,95,0.1)`,
    },
});

/**
 * GoldButton
 * Primary CTA button with a gold gradient.
 * Used exclusively for "Get in Touch" → /signup.
 * Dark text on gold background ensures high contrast/readability.
 */
const GoldButton = styled(Button)({
    background: `linear-gradient(135deg, ${BRAND.gold}, ${BRAND.goldenrod})`,
    color: BRAND.dark,
    fontFamily: "'Poppins', sans-serif",
    fontWeight: 700,
    fontSize: "0.85rem",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    padding: "12px 32px",
    borderRadius: "8px",
    border: "none",
    transition: "transform 0.25s ease, box-shadow 0.25s ease",
    "&:hover": {
        background: `linear-gradient(135deg, ${BRAND.goldenrod}, ${BRAND.gold})`,
        transform: "translateY(-2px)",
        boxShadow: `0 10px 28px rgba(229,201,95,0.35)`,
    },
});

/**
 * JourneyStepCard
 * Individual step row in the Gamified Journey Map.
 * Left border highlights to gold on hover as a subtle interactivity cue.
 */
const JourneyStepCard = styled(Box)({
    display: "flex",
    gap: "20px",
    alignItems: "flex-start",
    padding: "20px 24px",
    backgroundColor: BRAND.cardBg,
    border: `1px solid ${BRAND.border}`,
    borderRadius: "12px",
    borderLeft: `3px solid transparent`,
    transition: "border-left-color 0.3s ease, background-color 0.3s ease",
    "&:hover": {
        borderLeftColor: BRAND.gold,
        backgroundColor: "rgba(229,201,95,0.03)",
    },
});

/**
 * IconCircle
 * Circular container that wraps a MUI icon in a gold-tinted ring.
 * flexShrink: 0 prevents it from compressing inside flex parents.
 */
const IconCircle = styled(Box)({
    width: "50px",
    height: "50px",
    borderRadius: "50%",
    backgroundColor: "rgba(229,201,95,0.1)",
    border: `1px solid ${BRAND.border}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    color: BRAND.gold,
});

// ============================================================
//  DATA CONSTANTS
//  All page content lives here, separate from UI/JSX.
//  To edit any text or add new items — only change this section.
// ============================================================

/**
 * PROBLEM_ITEMS
 * Core problem → solution pairs in the Problem Statement section.
 * Each item has an MUI icon, a problem label, and its solution.
 */
const PROBLEM_ITEMS = [
    { icon: <TrendingUpIcon />,  problem: "Declining Interest",    solution: "Gamification reignites passion for coding" },
    { icon: <PsychologyIcon />,  problem: "AI Fear",               solution: "We teach humans to understand and command AI" },
    { icon: <SchoolIcon />,      problem: "No Clear Career Path",  solution: "Structured tracks lead to real certification" },
    { icon: <EmojiEventsIcon />, problem: "No Recognition",        solution: "Stars, golden buzzers & downloadable certificates" },
];

/**
 * IDENTITY_ITEMS
 * Five cards describing the platform's identity.
 * num is a decorative background label (01–05).
 */
const IDENTITY_ITEMS = [
    { num: "01", icon: <DevicesIcon />,          label: "SaaS Platform",   title: "Software as a Service",     desc: "Access from anywhere, on any device. Subscribe and start learning instantly — no installations, no barriers." },
    { num: "02", icon: <SchoolIcon />,           label: "EdTech Platform", title: "Educational Technology",    desc: "Structured, modern learning paths built for the next generation of software developers." },
    { num: "03", icon: <AutoAwesomeIcon />,      label: "Gamified Learning",title: "Stars, Buzzers & Rewards", desc: "Performance-based stars, golden buzzer moments, and achievement unlocks make every session exciting." },
    { num: "04", icon: <WorkspacePremiumIcon />, label: "Career Pathway",  title: "Zero to Certified",         desc: "A complete journey from absolute beginner to industry-certified professional developer." },
    { num: "05", icon: <GroupsIcon />,           label: "Talent Pipeline", title: "Job-Ready Developers",      desc: "We don't just teach — we produce competent, certified developers ready for the workforce." },
];

/**
 * WHY_MATTERS_ITEMS
 * Three cards explaining why the platform's mission matters in the AI era.
 */
const WHY_MATTERS_ITEMS = [
    { icon: <BoltIcon style={{backgroundColor:"gold", padding:"1px", borderRadius:"50%", height:"25px", weight:"15px"}}/>, title: "AI Amplifies, Not Replaces",  desc: "Developers who understand code will use AI as a superpower, shipping 10x faster than those who don't." },
    { icon: <PublicIcon style={{backgroundColor:"gold", padding:"1px", borderRadius:"50%", height:"25px", weight:"15px"}}/>, title: "Global Demand Grows",         desc: "The tech industry still needs millions of human developers to design, review, deploy, and maintain systems." },
    { icon: <LocalFireDepartmentIcon style={{backgroundColor:"gold", padding:"1px", borderRadius:"50%", height:"25px", weight:"15px"}}/>, title: "Passion Changes Everything",  desc: "We make coding fun and rewarding so learners build a genuine love for the craft — not just a job skill." },
];

/**
 * JOURNEY_STEPS
 * Six sequential steps in the gamified learning journey.
 * Each has an MUI icon, a title, and a short description.
 */
const JOURNEY_STEPS = [
    { icon: <RocketLaunchIcon />,        title: "Enroll & Choose Your Track",    desc: "Pick your developer path: Frontend, Backend, Fullstack, IoT, or Java/Python." },
    { icon: <CodeIcon />,                title: "Learn with Interactive Lessons", desc: "Bite-sized lessons, downloadable notes, and real code examples for hands-on learning." },
    { icon: <StarIcon />,                title: "Take Quizzes & Earn Stars",      desc: "Test your knowledge after each module. Earn stars based on your performance." },
    { icon: <NotificationsActiveIcon />, title: "Golden Buzzer Moments",          desc: "Score exceptionally well and unlock the Golden Buzzer — a mark of outstanding excellence." },
    { icon: <LightbulbIcon />,           title: "Final Certification Exam",       desc: "Prove your mastery with a comprehensive exam. Pass to unlock your certificate." },
    { icon: <VerifiedIcon />,            title: "Download Your Certificate",      desc: "You are now a certified developer. Share it, use it, and build a career with it." },
];

/**
 * CONSULT_SERVICES
 * Four consultancy service cards shown in the CTA section.
 */
const CONSULT_SERVICES = [
    { icon: <ArchitectureIcon />, title: "System Architecture", desc: "Design scalable, resilient software systems for your business." },
    { icon: <BugReportIcon />,    title: "Tech Audit & Review", desc: "Expert analysis of your codebase, security, and infrastructure." },
    { icon: <RocketLaunchIcon />, title: "MVP Development",     desc: "Turn your idea into a working product quickly and efficiently." },
    { icon: <SupportAgentIcon />, title: "Team Augmentation",   desc: "Extend your team with our certified developer graduates." },
];

// ============================================================
//  MAIN COMPONENT — Vision
// ============================================================

/**
 * Vision
 * Full-page Vision component for the platform.
 *
 * Page sections (in order):
 *  1. Hero            — animated headline + scroll indicator
 *  2. Problem         — core problem → solution pairs
 *  3. Identity        — what the platform truly is (5 cards)
 *  4. Why It Matters  — AI era argument (quote + 3 cards)
 *  5. Journey Map     — scroll-animated 6-step gamified path
 *  6. Consultancy CTA — services + Get in Touch → /signup
 *  7. Footer strip    — single-line brand statement
 *
 * Add this to App.js:
 *   import Vision from "./pages/Vision";
 *   <Route path="/vision" element={<Vision />} />
 */
export default function Vision() {
    // useNavigate — programmatic routing without full page reload
    const navigate = useNavigate();

    // ── Scroll-reveal state for journey steps ─────────────────
    // stepRefs holds a ref for each JourneyStepCard DOM node.
    // visibleSteps tracks which step indices are in the viewport.
    const stepRefs   = useRef([]);
    const [visibleSteps, setVisibleSteps] = useState([]);

    useEffect(() => {
        // Create one IntersectionObserver per step.
        // When a step enters the viewport, stagger its reveal by index * 120ms.
        const observers = stepRefs.current.map((el, i) => {
            if (!el) return null;
            const obs = new IntersectionObserver(
                ([entry]) => {
                    if (entry.isIntersecting) {
                        setTimeout(
                            () => setVisibleSteps((prev) => [...new Set([...prev, i])]),
                            i * 120
                        );
                    }
                },
                { threshold: 0.2 }
            );
            obs.observe(el);
            return obs;
        });
        // Always disconnect observers on unmount to prevent memory leaks
        return () => observers.forEach((o) => o && o.disconnect());
    }, []);

    // ── Render ────────────────────────────────────────────────
    return (
        <PageWrapper>

            {/* ════════════════════════════════════════════════════
          1. HERO SECTION
          Full-viewport intro with animated title and gold accent.
      ════════════════════════════════════════════════════ */}
            <HeroSection>
                <Container maxWidth="md" sx={{ position: "relative", zIndex: 2, py: 10 }}>

                    {/* Eyebrow badge */}
                    <Fade direction="down" triggerOnce>
                        <Box display="flex" justifyContent="center" mb={3}>
                            <GoldChip icon={<AutoAwesomeIcon sx={{ fontSize: 16 }} />} label="Our Vision" />
                        </Box>
                    </Fade>

                    {/* Main headline — "Builders" uses gold gradient text */}
                    <Fade direction="up" triggerOnce delay={200}>
                        <Typography
                            variant="h1"
                            sx={{
                                fontFamily: "'Poppins', sans-serif",
                                fontWeight: 800,
                                fontSize: { xs: "2.8rem", sm: "4rem", md: "5.5rem" },
                                lineHeight: 0.95,
                                letterSpacing: "-0.03em",
                                color: BRAND.white,
                                mb: 2,
                            }}
                        >
                            The Future{" "}
                            <Box
                                component="span"
                                sx={{
                                    // CSS background-clip trick for gradient text
                                    background: `linear-gradient(90deg, ${BRAND.gold}, ${BRAND.goldenrod})`,
                                    WebkitBackgroundClip: "text",
                                    WebkitTextFillColor: "transparent",
                                    backgroundClip: "text",
                                }}
                            >
                                Belongs to Builders
                            </Box>
                        </Typography>
                    </Fade>

                    {/* Subheading */}
                    <Fade direction="up" triggerOnce delay={400}>
                        <Typography
                            sx={{
                                fontFamily: "'Poppins', sans-serif",
                                fontSize: { xs: "1rem", md: "1.15rem" },
                                fontWeight: 300,
                                color: BRAND.dimWhite,
                                maxWidth: 560,
                                mx: "auto",
                                lineHeight: 1.8,
                                mt: 3,
                            }}
                        >
                            In a world where AI writes code, we train the humans who{" "}
                            <Box component="span" sx={{ color: BRAND.gold, fontWeight: 600 }}>
                                architect the future
                            </Box>
                            . Our vision is a world where becoming a developer is exciting, rewarding,
                            and within reach of anyone.
                        </Typography>
                    </Fade>

                    {/* Animated scroll indicator — pulsing vertical line */}
                    <Fade direction="up" triggerOnce delay={700}>
                        <Box mt={6} display="flex" flexDirection="column" alignItems="center" gap={1}>
                            <Typography
                                sx={{
                                    fontSize: "0.65rem",
                                    letterSpacing: "0.25em",
                                    color: BRAND.dimWhite,
                                    textTransform: "uppercase",
                                    fontFamily: "'Poppins', sans-serif",
                                }}
                            >
                                Explore
                            </Typography>
                            <Box
                                sx={{
                                    width: "1px",
                                    height: "50px",
                                    background: `linear-gradient(to bottom, ${BRAND.gold}, transparent)`,
                                    animation: "vPulse 2s ease-in-out infinite",
                                    "@keyframes vPulse": {
                                        "0%, 100%": { opacity: 0.3 },
                                        "50%":      { opacity: 1   },
                                    },
                                }}
                            />
                        </Box>
                    </Fade>
                </Container>
            </HeroSection>

            {/* ════════════════════════════════════════════════════
          2. CORE PROBLEM STATEMENT
          Left: bold quote. Right: problem → solution cards.
      ════════════════════════════════════════════════════ */}
            <SectionWrapper variant="alt">
                <Container maxWidth="lg">
                    <Grid container spacing={6} alignItems="center">

                        {/* Left column — problem quote */}
                        <Grid item xs={12} md={5}>
                            <Fade direction="left" triggerOnce>
                                <GoldChip label="The Core Problem" sx={{ mb: 2 }} />
                                <Typography
                                    sx={{
                                        fontFamily: "'Poppins', sans-serif",
                                        fontWeight: 700,
                                        fontSize: { xs: "1.6rem", md: "2.1rem" },
                                        lineHeight: 1.3,
                                        color: BRAND.white,
                                        letterSpacing: "-0.02em",
                                    }}
                                >
                                    People are losing interest in coding because{" "}
                                    <Box component="span" sx={{ color: BRAND.gold }}>
                                        AI seems to be doing it all
                                    </Box>{" "}
                                    — and that is the wrong conclusion.
                                </Typography>
                            </Fade>
                        </Grid>

                        {/* Right column — problem → solution rows */}
                        <Grid item xs={12} md={7}>
                            <Fade direction="right" triggerOnce>
                                <Box display="flex" flexDirection="column" gap={1.5}>
                                    {PROBLEM_ITEMS.map((item, i) => (
                                        <Box
                                            key={i}
                                            sx={{
                                                display: "grid",
                                                gridTemplateColumns: "44px 1fr 28px",
                                                gap: "16px",
                                                alignItems: "center",
                                                p: "14px 20px",
                                                backgroundColor: BRAND.cardBg,
                                                border: `1px solid ${BRAND.border}`,
                                                borderRadius: "12px",
                                                transition: "border-color 0.3s",
                                                "&:hover": { borderColor: BRAND.gold },
                                            }}
                                        >
                                            <Box sx={{ color: BRAND.gold, display: "flex" }}>{item.icon}</Box>
                                            <Box>
                                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600, fontSize: "0.9rem", color: BRAND.white }}>
                                                    {item.problem}
                                                </Typography>
                                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.81rem", color: BRAND.dimWhite, mt: 0.3 }}>
                                                    {item.solution}
                                                </Typography>
                                            </Box>
                                            <Typography sx={{ color: BRAND.gold, fontWeight: 700, fontSize: "1.1rem" }}>→</Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </Fade>
                        </Grid>
                    </Grid>
                </Container>
            </SectionWrapper>

            {/* ════════════════════════════════════════════════════
          3. PLATFORM IDENTITY
          Five feature cards — what the platform truly is.
      ════════════════════════════════════════════════════ */}
            <SectionWrapper>
                <Container maxWidth="lg">
                    <Fade direction="up" triggerOnce>
                        <Box textAlign="center" mb={6}>
                            <GoldChip label="Platform Identity" sx={{ mb: 2 }} />
                            <Typography
                                variant="h2"
                                sx={{
                                    fontFamily: "'Poppins', sans-serif",
                                    fontWeight: 800,
                                    fontSize: { xs: "2rem", md: "2.8rem" },
                                    color: BRAND.white,
                                    letterSpacing: "-0.02em",
                                }}
                            >
                                What We Truly Are
                            </Typography>
                            <GoldDivider />
                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", color: BRAND.dimWhite, mt: 2, maxWidth: 520, mx: "auto", fontSize: "0.95rem", lineHeight: 1.7 }}>
                                Not just another coding platform — a complete developer ecosystem from first lesson to first job.
                            </Typography>
                        </Box>
                    </Fade>

                    <Grid container spacing={3}>
                        {IDENTITY_ITEMS.map((item, i) => (
                            <Grid item xs={12} sm={6} md={4} key={i}>
                                <Fade direction="up" triggerOnce delay={i * 100}>
                                    <FeatureCard>
                                        <CardContent sx={{ p: 3 }}>
                                            {/* Decorative large number — barely visible behind content */}
                                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 800, fontSize: "2.5rem", color: "rgba(229,201,95,0.07)", lineHeight: 1, mb: 1 }}>
                                                {item.num}
                                            </Typography>
                                            <IconCircle sx={{ mb: 1.5 }}>{item.icon}</IconCircle>
                                            <GoldChip label={item.label} size="small" sx={{ mb: 1.5 }} />
                                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: "1.05rem", color: BRAND.white, mb: 1 }}>
                                                {item.title}
                                            </Typography>
                                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.85rem", color: BRAND.dimWhite, lineHeight: 1.65 }}>
                                                {item.desc}
                                            </Typography>
                                        </CardContent>
                                    </FeatureCard>
                                </Fade>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </SectionWrapper>

            {/* ════════════════════════════════════════════════════
          4. WHY THIS MATTERS
          Bold AI-era quote block + three reason cards.
      ════════════════════════════════════════════════════ */}
            <SectionWrapper variant="alt">
                <Container maxWidth="lg">
                    <Fade direction="up" triggerOnce>
                        <Box textAlign="center" mb={6}>
                            <GoldChip label="Why This Matters" sx={{ mb: 2 }} />
                            <Typography
                                variant="h2"
                                sx={{
                                    fontFamily: "'Poppins', sans-serif",
                                    fontWeight: 800,
                                    fontSize: { xs: "2rem", md: "2.8rem" },
                                    color: BRAND.white,
                                    letterSpacing: "-0.02em",
                                }}
                            >
                                The AI Era Needs More Developers, Not Fewer
                            </Typography>
                            <GoldDivider />
                        </Box>
                    </Fade>

                    {/* Highlighted platform quote — left gold border treatment */}
                    <Fade direction="up" triggerOnce delay={150}>
                        <Box
                            sx={{
                                maxWidth: 780,
                                mx: "auto",
                                mb: 6,
                                p: { xs: 3, md: 4 },
                                borderLeft: `4px solid ${BRAND.gold}`,
                                backgroundColor: "rgba(229,201,95,0.04)",
                                borderRadius: "0 12px 12px 0",
                            }}
                        >
                            <Typography
                                sx={{
                                    fontFamily: "'Poppins', sans-serif",
                                    fontWeight: 700,
                                    fontSize: { xs: "1.1rem", md: "1.45rem" },
                                    color: BRAND.white,
                                    lineHeight: 1.5,
                                    letterSpacing: "-0.01em",
                                }}
                            >
                                "AI writes code — but{" "}
                                <Box component="span" sx={{ color: BRAND.gold }}>humans architect solutions</Box>. AI generates functions — but{" "}
                                <Box component="span" sx={{ color: "gold" }}>humans understand problems</Box>. The developer of tomorrow doesn't compete with AI —{" "}
                                <Box component="span" sx={{ color: BRAND.goldenrod }}>they command it</Box>."
                            </Typography>
                        </Box>
                    </Fade>

                    <Grid container spacing={3}>
                        {WHY_MATTERS_ITEMS.map((item, i) => (
                            <Grid item xs={12} md={4} key={i}>
                                <Fade direction="up" triggerOnce delay={i * 120}>
                                    <FeatureCard>
                                        <CardContent sx={{ p: 3 }}>
                                            <Typography sx={{ fontSize: "2rem", mb: 1.5 }}>{item.icon}</Typography>
                                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: "1rem", color: BRAND.gold, mb: 1 }}>
                                                {item.title}
                                            </Typography>
                                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.875rem", color: BRAND.dimWhite, lineHeight: 1.65 }}>
                                                {item.desc}
                                            </Typography>
                                        </CardContent>
                                    </FeatureCard>
                                </Fade>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </SectionWrapper>

            {/* ════════════════════════════════════════════════════
          5. GAMIFIED JOURNEY MAP
          Scroll-animated six-step timeline.
          Steps fade + slide in as the user scrolls down.
      ════════════════════════════════════════════════════ */}
            <SectionWrapper>
                <Container maxWidth="md">
                    <Fade direction="up" triggerOnce>
                        <Box textAlign="center" mb={6}>
                            <GoldChip label="The Gamified Journey" sx={{ mb: 2 }} />
                            <Typography
                                variant="h2"
                                sx={{
                                    fontFamily: "'Poppins', sans-serif",
                                    fontWeight: 800,
                                    fontSize: { xs: "2rem", md: "2.8rem" },
                                    color: BRAND.white,
                                    letterSpacing: "-0.02em",
                                }}
                            >
                                Your Path from Learner to Developer
                            </Typography>
                            <GoldDivider />
                        </Box>
                    </Fade>

                    {/* Timeline container — ::before draws the vertical gold line */}
                    <Box
                        sx={{
                            position: "relative",
                            "&::before": {
                                content: '""',
                                position: "absolute",
                                left: "25px",
                                top: "30px",
                                bottom: "30px",
                                width: "2px",
                                background: `linear-gradient(to bottom, ${BRAND.gold}, ${BRAND.goldenrod}, transparent)`,
                                display: { xs: "none", sm: "block" },
                            },
                        }}
                    >
                        <Box display="flex" flexDirection="column" gap={2} pl={{ xs: 0, sm: "60px" }}>
                            {JOURNEY_STEPS.map((step, i) => (
                                /*
                                 * Each step has a ref attached so IntersectionObserver can
                                 * detect when it enters the viewport and trigger the CSS transition.
                                 * The opacity/transform are driven by visibleSteps state above.
                                 */
                                <JourneyStepCard
                                    key={i}
                                    ref={(el) => (stepRefs.current[i] = el)}
                                    sx={{
                                        opacity:   visibleSteps.includes(i) ? 1 : 0,
                                        transform: visibleSteps.includes(i) ? "translateX(0)" : "translateX(-20px)",
                                        transition: `opacity 0.5s ease ${i * 0.1}s, transform 0.5s ease ${i * 0.1}s`,
                                    }}
                                >
                                    <IconCircle sx={{ mt: 0.3 }}>{step.icon}</IconCircle>
                                    <Box>
                                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: "1rem", color: BRAND.white, mb: 0.4 }}>
                                            {step.title}
                                        </Typography>
                                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.85rem", color: BRAND.dimWhite, lineHeight: 1.6 }}>
                                            {step.desc}
                                        </Typography>
                                    </Box>
                                </JourneyStepCard>
                            ))}
                        </Box>
                    </Box>
                </Container>
            </SectionWrapper>

            {/* ════════════════════════════════════════════════════
          6. SOFTWARE CONSULTANCY CTA
          Left: 4 service cards.
          Right: CTA box with GoldButton → /signup.
      ════════════════════════════════════════════════════ */}
            <SectionWrapper variant="alt">
                <Container maxWidth="lg">
                    <Grid container spacing={6} alignItems="center">

                        {/* Left: consultancy services */}
                        <Grid item xs={12} md={7}>
                            <Fade direction="left" triggerOnce>
                                <GoldChip label="Software Consultancy" sx={{ mb: 2 }} />
                                <Typography
                                    variant="h2"
                                    sx={{
                                        fontFamily: "'Poppins', sans-serif",
                                        fontWeight: 800,
                                        fontSize: { xs: "2rem", md: "2.5rem" },
                                        color: BRAND.white,
                                        letterSpacing: "-0.02em",
                                        mb: 1.5,
                                    }}
                                >
                                    We Build. We Advise. We Deliver.
                                </Typography>
                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.95rem", color: BRAND.dimWhite, lineHeight: 1.7, mb: 3 }}>
                                    Beyond training, we offer professional software consultancy to businesses
                                    looking to build, scale, or modernise their technology.
                                </Typography>
                                <Grid container spacing={2}>
                                    {CONSULT_SERVICES.map((s, i) => (
                                        <Grid item xs={12} sm={6} key={i}>
                                            <Box
                                                sx={{
                                                    p: 2,
                                                    backgroundColor: BRAND.cardBg,
                                                    border: `1px solid ${BRAND.border}`,
                                                    borderRadius: "12px",
                                                    transition: "border-color 0.3s",
                                                    "&:hover": { borderColor: BRAND.gold },
                                                }}
                                            >
                                                <Box sx={{ color: BRAND.gold, mb: 0.75, display: "flex" }}>{s.icon}</Box>
                                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: "0.9rem", color: BRAND.white, mb: 0.4 }}>
                                                    {s.title}
                                                </Typography>
                                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.8rem", color: BRAND.dimWhite, lineHeight: 1.55 }}>
                                                    {s.desc}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Fade>
                        </Grid>

                        {/* Right: CTA card → /signup */}
                        <Grid item xs={12} md={5}>
                            <Fade direction="right" triggerOnce delay={200}>
                                <Box
                                    sx={{
                                        p: 4,
                                        backgroundColor: BRAND.cardBg,
                                        border: `1px solid ${BRAND.border}`,
                                        borderRadius: "20px",
                                        textAlign: "center",
                                        backdropFilter: "blur(10px)",
                                    }}
                                >
                                    <BusinessCenterIcon sx={{ fontSize: 48, color: BRAND.gold, mb: 2 }} />
                                    <Typography
                                        sx={{
                                            fontFamily: "'Poppins', sans-serif",
                                            fontWeight: 800,
                                            fontSize: "1.5rem",
                                            color: BRAND.white,
                                            mb: 1.5,
                                            letterSpacing: "-0.01em",
                                        }}
                                    >
                                        Ready to Build Something Great?
                                    </Typography>
                                    <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.9rem", color: BRAND.dimWhite, lineHeight: 1.7, mb: 3 }}>
                                        Whether you're a startup needing your first MVP or an enterprise modernising
                                        your stack — we are your technical partner.
                                    </Typography>
                                    {/*
                   * GoldButton — onClick calls navigate("/signup").
                   * react-router's navigate() does a client-side redirect
                   * without reloading the page, keeping SPA behaviour.
                   */}
                                    <GoldButton fullWidth onClick={() => navigate("/signup")} startIcon={<RocketLaunchIcon />}>
                                        Get in Touch
                                    </GoldButton>
                                </Box>
                            </Fade>
                        </Grid>
                    </Grid>
                </Container>
            </SectionWrapper>

            {/* ════════════════════════════════════════════════════
          7. FOOTER STRIP
          Single-line brand statement at the very bottom.
      ════════════════════════════════════════════════════ */}
            <Box sx={{ borderTop: `1px solid ${BRAND.border}`, py: 2.5, textAlign: "center" }}>
                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.8rem", color: BRAND.dimWhite }}>
                    <Box component="span" sx={{ color: BRAND.gold, fontWeight: 700 }}>Our Vision: </Box>
                    A world where every passionate person can become a skilled, certified, and employed developer.
                </Typography>
            </Box>

        </PageWrapper>
    );
}
